import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { zodResolver } from '@hookform/resolvers/zod';
import CompanyHeader from '@/components/company/CompanyHeader';
import FormBuilder from '@/components/company/FormBuilder';
import type { FormField } from '@/components/company/FormBuilder';
import GenerateAIModal from '@/components/company/GenerateAIModal';
import AIResultModal from '@/components/company/AIResultModal';
import MissionAIGenerateForm from './mission/MissionAIGenerateForm';
import useCompanyState from '@/states/company.state';
import useTenantState from '@/states/tenant.state';
import { useGenerateMission } from './mission/useGenerateMission';
import { missionAIGenerateSchema, type MissionAIGenerateFormData } from './mission/mission-ai-generate.schema';

interface MissionFormValues {
  human_resources: string;
  operation: string;
  marketing: string;
  finance: string;
}

export default function Mission() {
  const formControl = useForm<MissionFormValues>({
    defaultValues: {
      human_resources: '',
      operation: '',
      marketing: '',
      finance: '',
    },
  });
  const { handleSubmit, reset, setValue } = formControl;
  const formRef = useRef<HTMLFormElement>(null);

  const aiFormControl = useForm<MissionAIGenerateFormData>({
    resolver: zodResolver(missionAIGenerateSchema),
    defaultValues: {
      business_activities: '',
      hr_focus: '',
      operational_focus: '',
      marketing_focus: '',
      finance_focus: '',
    },
  });

  const { companyProfile, updateCompanyField, isLoading } = useCompanyState();
  const { currentTenant } = useTenantState();
  const [isPublished, setIsPublished] = useState(false);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [generatedOptions, setGeneratedOptions] = useState<string[]>([]);
  const [parsedMissionOptions, setParsedMissionOptions] = useState<MissionFormValues[]>([]);

  const generateMissionMutation = useGenerateMission({
    tenantId: currentTenant?.id ? String(currentTenant.id) : '',
  });

  useEffect(() => {
    if (!companyProfile) return;

    const missionData = Array.isArray(companyProfile.mission) ? companyProfile.mission[0] : null;

    if (missionData) {
      reset({
        human_resources: missionData.human_resources ?? '',
        operation: missionData.operation ?? '',
        marketing: missionData.marketing ?? '',
        finance: missionData.finance ?? '',
      });
    }

    const publishedValue = companyProfile.is_published_mission;
    setIsPublished(!!publishedValue);
  }, [companyProfile, reset]);

  const handleSave = async (data: MissionFormValues) => {
    if (!currentTenant?.id) return;
    const payload = [
      {
        human_resources: data.human_resources,
        operation: data.operation,
        marketing: data.marketing,
        finance: data.finance,
      },
    ];
    updateCompanyField(currentTenant.id, 'mission', payload, isPublished);
  };

  const handleOpenGenerateModal = () => {
    aiFormControl.reset();
    setIsGenerateModalOpen(true);
  };

  const handleCloseGenerateModal = () => {
    setIsGenerateModalOpen(false);
  };

  const handleGenerate = async (data: MissionAIGenerateFormData) => {
    try {
      const result = await generateMissionMutation.mutateAsync(data);

      if (result.success && result.data?.generated_options) {
        const parsed = result.data.generated_options.map((option: string) => {
          try {
            const jsonData = JSON.parse(option);
            return {
              human_resources: jsonData.human_resources || '',
              operation: jsonData.operation || '',
              marketing: jsonData.marketing || '',
              finance: jsonData.finance || '',
            };
          } catch (e) {
            console.error(e);
            return {
              human_resources: '',
              operation: '',
              marketing: '',
              finance: '',
            };
          }
        });

        setParsedMissionOptions(parsed);

        const displayOptions = parsed.map((mission: MissionFormValues, index: number) => {
          return `**Human Resources:** ${mission.human_resources}\n\n**Operation:** ${mission.operation}\n\n**Marketing:** ${mission.marketing}\n\n**Finance:** ${mission.finance}`;
        });

        setGeneratedOptions(displayOptions);
        setIsGenerateModalOpen(false);
        setIsResultModalOpen(true);
        toast.success('Mission generated successfully');
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate mission');
    }
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    setGeneratedOptions([]);
    setParsedMissionOptions([]);
  };

  const handleSaveGeneratedMission = (selectedOption: string) => {
    const selectedIndex = generatedOptions.indexOf(selectedOption);

    if (selectedIndex !== -1 && parsedMissionOptions[selectedIndex]) {
      const mission = parsedMissionOptions[selectedIndex];
      setValue('human_resources', mission.human_resources);
      setValue('operation', mission.operation);
      setValue('marketing', mission.marketing);
      setValue('finance', mission.finance);
    }

    setIsResultModalOpen(false);
    setGeneratedOptions([]);
    setParsedMissionOptions([]);
  };

  const handleAfterSave = async () => {
    if (!currentTenant?.id) return;
    const currentData = formControl.getValues();
    const payload = [
      {
        human_resources: currentData.human_resources,
        operation: currentData.operation,
        marketing: currentData.marketing,
        finance: currentData.finance,
      },
    ];
    await updateCompanyField(currentTenant.id, 'mission', payload, isPublished);
    toast.success('Mission saved successfully');
  };

  const fields: FormField[] = [
    {
      name: 'human_resources',
      label: 'Human Resources Mission',
      type: 'markdown',
      placeholder: "Enter your company's mission statement...",
    },
    {
      name: 'operation',
      label: 'Operation Mission',
      type: 'markdown',
      placeholder: 'Enter your operation mission...',
    },
    {
      name: 'marketing',
      label: 'Marketing Mission',
      type: 'markdown',
      placeholder: 'Enter your marketing mission...',
    },
    {
      name: 'finance',
      label: 'Finance Mission',
      type: 'markdown',
      placeholder: 'Enter your finance mission...',
    },
  ];

  return (
    <section className="w-full">
      <CompanyHeader
        title="Mission Statement"
        description="Define your company's mission for each department: HR, Operation, Marketing, and Finance."
        onGenerate={handleOpenGenerateModal}
        generateLabel="Generate"
        saveLabel="Save"
        onSave={() => formRef.current?.requestSubmit()}
        isSaving={isLoading}
      />

      <div className="mx-5 border-b border-gray-200" />

      <FormProvider {...formControl}>
        <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="pt-5">
          <FormBuilder fields={fields} initialPublished={isPublished} onPublishChange={setIsPublished} />
        </form>
      </FormProvider>

      <GenerateAIModal
        isOpen={isGenerateModalOpen}
        onClose={handleCloseGenerateModal}
        onSubmit={aiFormControl.handleSubmit(handleGenerate)}
        title="Generate Mission with AI"
        description="Provide the information below to help AI generate mission statements for each department."
        generateButtonLabel="Generate Mission"
        isLoading={generateMissionMutation.isLoading}
      >
        <MissionAIGenerateForm
          control={aiFormControl.control}
          errors={aiFormControl.formState.errors}
          isLoading={generateMissionMutation.isLoading}
        />
      </GenerateAIModal>

      <AIResultModal
        isOpen={isResultModalOpen}
        onClose={handleCloseResultModal}
        options={generatedOptions}
        onSave={handleSaveGeneratedMission}
        onAfterSave={handleAfterSave}
        title="AI Generated Mission"
        description="Select the mission statements that best represent your company"
        saveButtonLabel="Save to Profile"
        isLoading={isLoading}
      />
    </section>
  );
}
